import { r, s } from '@/theme';
import { componentSizes } from '@/theme/componentSizes';
import { pickReadableTextColor } from './colorUtils';
import { useTheme } from './hooks';
import { RawChip } from './RawChip';
import { type ChipColorKey, getChipSxForMode, mergeChipSx } from './statusChips';
import { Text } from './Text';
import type { ChipProps as MuiChipProps } from '@mui/material/Chip';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement, ReactNode } from 'react';

export type StatusTone =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'brown'
  | 'primary'
  | 'purple'
  | 'pink'
  | 'default'
  | 'neutral'
  | 'green'
  | 'red'
  | 'orange'
  | 'blue'
  | 'grey'
  | 'yellow'
  | 'teal'
  | 'transparent';

export type ChipSize = 'xs' | 'small' | 'medium';

export type ChipProps = Omit<MuiChipProps, 'color' | 'size' | 'label' | 'sx'> & {
  label: ReactNode;
  tone?: StatusTone;
  color?: StatusTone;
  size?: ChipSize;
  transform?: 'uppercase' | 'none';
  sx?: SxProps<Theme>;
};

type NormalizedTone = Exclude<
  StatusTone,
  'neutral' | 'green' | 'red' | 'orange' | 'blue' | 'grey' | 'yellow' | 'teal' | 'transparent'
>;

const normalizeTone = (tone: StatusTone): NormalizedTone => {
  switch (tone) {
    case 'neutral':
    case 'grey':
      return 'default';
    case 'green':
      return 'success';
    case 'red':
      return 'error';
    case 'orange':
    case 'yellow':
      return 'warning';
    case 'blue':
      return 'info';
    case 'teal':
      return 'primary';
    case 'transparent':
      return 'default';
    case 'success':
    case 'warning':
    case 'error':
    case 'info':
    case 'brown':
    case 'primary':
    case 'purple':
    case 'pink':
    case 'default':
      return tone;
  }
  const _exhaustive: never = tone;
  return _exhaustive;
};

type ResolvedTone = NormalizedTone | 'transparent';

const resolveTone = (tone?: StatusTone, color?: StatusTone): ResolvedTone => {
  if (tone === 'transparent' || color === 'transparent') return 'transparent';
  if (tone) return normalizeTone(tone);
  if (color) return normalizeTone(color);
  return 'default';
};

const resolveChipColorKey = (tone: NormalizedTone): ChipColorKey => {
  switch (tone) {
    case 'success':
      return 'green';
    case 'error':
      return 'red';
    case 'warning':
      return 'yellow';
    case 'info':
      return 'blue';
    case 'brown':
      return 'brown';
    case 'primary':
      return 'teal';
    case 'purple':
      return 'purple';
    case 'pink':
      return 'pink';
    case 'default':
      return 'grey';
  }
  const _exhaustive: never = tone;
  return _exhaustive;
};

export const Chip = ({
  label,
  tone,
  color,
  size = 'small',
  transform = 'none',
  onClick,
  clickable,
  variant,
  sx,
  ...rest
}: ChipProps): ReactElement => {
  const theme = useTheme();
  const resolvedTone = resolveTone(tone, color);
  const chipColorKey = resolvedTone === 'transparent' ? null : resolveChipColorKey(resolvedTone);
  const resolvedSize = size === 'xs' ? 'small' : size;
  const labelPadding =
    size === 'xs' || size === 'small'
      ? { px: s('sm'), py: s('none') }
      : { px: s('md'), py: s('xxs') };
  const resolvedLabel =
    typeof label === 'string' || typeof label === 'number' ? (
      <Text role="body" component="span" color="inherit">
        {label}
      </Text>
    ) : (
      label
    );
  const baseSx: SxProps<Theme> = {
    borderRadius: r('chip'),
    fontWeight: 500,
    ...(size === 'xs'
      ? { height: componentSizes.chips.xsHeight }
      : size === 'small'
        ? { height: 22 }
        : {}),
    '& .MuiChip-label': { textTransform: transform, ...labelPadding },
    '& .MuiChip-label .MuiTypography-root': {
      fontWeight: 500,
      textTransform: transform,
      fontSize: '11px',
      lineHeight: 1.2,
    },
  };
  const transparentToneSx: SxProps<Theme> = {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'text.primary',
    '& .MuiChip-label': { color: 'text.primary' },
    '& .MuiChip-icon': { color: 'text.primary' },
  };
  const baseChipSx =
    chipColorKey === null
      ? mergeChipSx(baseSx, transparentToneSx)
      : mergeChipSx(getChipSxForMode(theme.palette.mode, chipColorKey), baseSx);
  const baseBackground =
    chipColorKey === null
      ? undefined
      : (baseChipSx as { backgroundColor?: string }).backgroundColor;
  const contrastText = baseBackground
    ? pickReadableTextColor(
        baseBackground,
        theme.palette.text.primary,
        theme.palette.common.black,
        theme.palette.common.white,
      )
    : undefined;
  const contrastSx: SxProps<Theme> | undefined = contrastText
    ? {
        color: contrastText,
        '& .MuiChip-label': { color: contrastText },
        '& .MuiChip-label .MuiTypography-root': { color: contrastText },
        '& .MuiChip-icon': { color: contrastText },
      }
    : undefined;
  const baseWithContrast = contrastSx ? mergeChipSx(baseChipSx, contrastSx) : baseChipSx;
  const interactionSx: SxProps<Theme> =
    baseBackground === undefined
      ? {
          '&:hover, &.Mui-focusVisible, &:active': {
            backgroundColor: 'transparent',
          },
        }
      : {
          '&:hover, &.Mui-focusVisible, &:active': {
            backgroundColor: baseBackground,
          },
        };
  const mergedSx = Array.isArray(sx)
    ? [baseWithContrast, interactionSx, ...sx]
    : sx
      ? mergeChipSx(mergeChipSx(baseWithContrast, interactionSx), sx)
      : mergeChipSx(baseWithContrast, interactionSx);

  return (
    <RawChip
      label={resolvedLabel}
      size={resolvedSize}
      variant={variant ?? 'outlined'}
      onClick={onClick}
      clickable={clickable}
      sx={mergedSx}
      {...rest}
    />
  );
};
